import React, { useEffect, useState } from 'react';
import Notiflix from 'notiflix';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../components/CustomHook';

const EMAILJS_PUBLIC_KEY =
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'hj9cK5Vzo1uhcuMQa';
const EMAILJS_SERVICE_ID =
    process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_8724n0k';
const EMAILJS_TEMPLATE_ID =
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_w9s72yd';

const PAGE_COPY = {
    nl: {
        docTitle: 'D&IT | Intakeformulier',
        eyebrow: 'Project intake',
        heroTitle: 'Vertel ons wat je wilt bouwen',
        heroDescription:
            'Voor websites, boekingsplatformen, dashboards en ander maatwerk. Hoe vollediger je antwoorden, hoe sneller we scope, timing en budget correct kunnen inschatten.',
        standaloneBadge: 'Losse pagina',
        standaloneDescription:
            'Deze intake staat los van de hoofdsite en is enkel bereikbaar via de rechtstreekse URL.',
        heroPoints: [
            'Snellere eerste analyse',
            'Duidelijkere scope en timing',
            'Minder heen-en-weer in de opstart',
        ],
        formTitle: 'Intakeformulier',
        formDescription:
            'Je hoeft niet overal al een exact antwoord op te hebben. Gebruik gerust "Nog te bespreken" waar nodig.',
        outputTitle: 'Voorbeeld van verzending',
        outputPlaceholder:
            'Vul het formulier in om hier een live samenvatting te zien van wat er verzonden zal worden.',
        actionsTitle: 'Versturen',
        actionsDescription:
            'Bij verzenden mailen we deze intake rechtstreeks naar ons via dezelfde mailflow als het contactformulier. Kopieren kan nog steeds als back-up.',
        sendIntake: 'Verstuur intake',
        sending: 'Bezig met verzenden...',
        copySummary: 'Kopieer samenvatting',
        reset: 'Leegmaken',
        sendSuccess: 'Intake succesvol verzonden.',
        sendError:
            'Verzenden lukte niet. Probeer het opnieuw of stuur ons rechtstreeks een e-mail met de samenvatting hieronder.',
        missingContact:
            'Vul minstens een contactnaam en e-mailadres in voor we de intake kunnen versturen.',
        copySuccess: 'Samenvatting gekopieerd naar het klembord.',
        copyError:
            'Automatisch kopieren lukte niet. Je kunt de tekst hieronder manueel kopieren.',
        summaryHeading: 'PROJECTINTAKE',
    },
    en: {
        docTitle: 'D&IT | Project Intake',
        eyebrow: 'Project intake',
        heroTitle: 'Tell us what you want to build',
        heroDescription:
            'For websites, booking platforms, dashboards, and custom software. The more complete your answers are, the faster we can estimate scope, timing, and budget.',
        standaloneBadge: 'Standalone page',
        standaloneDescription:
            'This intake lives outside the main site flow and is only available through its direct URL.',
        heroPoints: [
            'Faster first analysis',
            'Clearer scope and timing',
            'Less back-and-forth during kickoff',
        ],
        formTitle: 'Project intake form',
        formDescription:
            'You do not need a perfect answer for every question. Feel free to use "To be discussed" where needed.',
        outputTitle: 'Email preview',
        outputPlaceholder:
            'Fill in the form to see a live preview of what will be sent.',
        actionsTitle: 'Send intake',
        actionsDescription:
            'Submitting this form emails the full intake to us through the same mail flow as the contact form. Copying the summary remains available as a backup.',
        sendIntake: 'Send intake',
        sending: 'Sending...',
        copySummary: 'Copy summary',
        reset: 'Clear form',
        sendSuccess: 'Intake sent successfully.',
        sendError:
            'Sending failed. Please try again or email us directly with the summary shown below.',
        missingContact:
            'Please fill in at least a contact name and email address before sending the intake.',
        copySuccess: 'Summary copied to clipboard.',
        copyError:
            'Automatic copy did not work here. You can still copy the text manually below.',
        summaryHeading: 'PROJECT INTAKE',
    },
};

const option = (value, nl, en) => ({
    value,
    label: { nl, en },
});

const textField = (key, label, type = 'text', config = {}) => ({
    key,
    type,
    label,
    ...config,
});

const textareaField = (key, label, config = {}) => ({
    key,
    type: 'textarea',
    label,
    rows: config.rows || 4,
    fullWidth: config.fullWidth ?? true,
    ...config,
});

const radioField = (key, label, options, config = {}) => ({
    key,
    type: 'radio',
    label,
    options,
    optionColumns: config.optionColumns || 1,
    fullWidth: config.fullWidth ?? false,
    ...config,
});

const checkboxField = (key, label, options, config = {}) => ({
    key,
    type: 'checkbox',
    label,
    options,
    optionColumns: config.optionColumns || 2,
    fullWidth: config.fullWidth ?? true,
    ...config,
});

const YES_NO_NOT_SURE = [
    option('yes', 'Ja', 'Yes'),
    option('no', 'Nee', 'No'),
    option('notSure', 'Niet zeker', 'Not sure'),
];

const YES_NO_DISCUSS = [
    option('yes', 'Ja', 'Yes'),
    option('no', 'Nee', 'No'),
    option('discuss', 'Nog te bespreken', 'To be discussed'),
];

const YES_NO_MAYBE = [
    option('yes', 'Ja', 'Yes'),
    option('no', 'Nee', 'No'),
    option('maybe', 'Misschien', 'Maybe'),
];

const FORM_SECTIONS = [
    {
        id: 'basisgegevens',
        title: {
            nl: '1. Basisgegevens',
            en: '1. Basic details',
        },
        fields: [
            textField('contactNaam', {
                nl: 'Naam contactpersoon',
                en: 'Contact name',
            }),
            textField('bedrijfsnaam', {
                nl: 'Bedrijfsnaam',
                en: 'Company name',
            }),
            textField(
                'email',
                {
                    nl: 'E-mailadres',
                    en: 'Email address',
                },
                'email',
            ),
            textField(
                'telefoon',
                {
                    nl: 'Telefoonnummer',
                    en: 'Phone number',
                },
                'tel',
            ),
            textField(
                'website',
                {
                    nl: 'Website of social media pagina',
                    en: 'Website or social media page',
                },
                'url',
                { fullWidth: true },
            ),
        ],
    },
    {
        id: 'zaak',
        title: {
            nl: '2. Over jullie zaak',
            en: '2. About your business',
        },
        fields: [
            textareaField('zaakOmschrijving', {
                nl: 'Kan je kort uitleggen wat jullie zaak precies doet?',
                en: 'Could you briefly explain what your business does?',
            }),
            checkboxField(
                'diensten',
                {
                    nl: 'Welke diensten bieden jullie aan?',
                    en: 'What services do you offer?',
                },
                [
                    option('dogHotel', 'Hondenhotel', 'Dog hotel'),
                    option('dayCare', 'Dagopvang', 'Day care'),
                    option('grooming', 'Trimsalon', 'Grooming'),
                    option('training', 'Training / begeleiding', 'Training / coaching'),
                    option('petSitting', 'Oppas', 'Pet sitting'),
                    option('transport', 'Vervoer / taxi', 'Transport / taxi'),
                    option('other', 'Andere', 'Other'),
                ],
                { optionColumns: 2 },
            ),
            textField(
                'dienstenAndere',
                {
                    nl: 'Indien andere, welke?',
                    en: 'If other, which ones?',
                },
                'text',
                { fullWidth: true },
            ),
        ],
    },
    {
        id: 'projectDoel',
        title: {
            nl: '3. Doel van het project',
            en: '3. Project goals',
        },
        fields: [
            checkboxField(
                'doelen',
                {
                    nl: 'Wat willen jullie met de website vooral bereiken?',
                    en: 'What do you mainly want the website to achieve?',
                },
                [
                    option('newCustomers', 'Nieuwe klanten aantrekken', 'Attract new customers'),
                    option('onlineReservations', 'Online reservaties mogelijk maken', 'Enable online reservations'),
                    option('lessAdmin', 'Minder administratie / manueel werk', 'Reduce admin / manual work'),
                    option('onlinePayments', 'Online betalingen ontvangen', 'Receive online payments'),
                    option('professionalPresence', 'Professioneler online zichtbaar zijn', 'Look more professional online'),
                    option('centralizeClientData', 'Klantinformatie centraliseren', 'Centralize client information'),
                    option('other', 'Andere', 'Other'),
                ],
                { optionColumns: 2 },
            ),
            textareaField('probleemVandaag', {
                nl: 'Wat is vandaag het grootste probleem of de grootste frustratie in jullie huidige manier van werken?',
                en: 'What is the biggest frustration in your current way of working today?',
            }),
            textareaField('succesDefinitie', {
                nl: 'Wat moet deze website absoluut kunnen om voor jullie een succes te zijn?',
                en: 'What must this website absolutely do to be considered a success?',
            }),
        ],
    },
    {
        id: 'boekingen',
        title: {
            nl: '4. Boekingen en werking',
            en: '4. Bookings and operations',
        },
        fields: [
            textareaField('boekbareDiensten', {
                nl: 'Welke diensten moeten online boekbaar zijn?',
                en: 'What services should be bookable online?',
            }),
            textareaField('reservatieVandaag', {
                nl: 'Hoe werkt een reservatie vandaag?',
                en: 'How does a reservation work today?',
            }),
            radioField(
                'meerdereHonden',
                {
                    nl: 'Moeten klanten meerdere honden tegelijk kunnen boeken?',
                    en: 'Should customers be able to book multiple dogs at once?',
                },
                YES_NO_NOT_SURE,
            ),
            radioField(
                'bevestiging',
                {
                    nl: 'Moet een reservatie automatisch bevestigd worden?',
                    en: 'Should a reservation be confirmed automatically?',
                },
                [
                    option('automatic', 'Automatisch bevestigen', 'Confirm automatically'),
                    option('manual', 'Eerst manuele goedkeuring', 'Require manual approval first'),
                    option('discuss', 'Nog te bespreken', 'To be discussed'),
                ],
            ),
            radioField(
                'wijzigenAnnuleren',
                {
                    nl: 'Moeten klanten hun reservatie zelf kunnen wijzigen of annuleren?',
                    en: 'Should customers be able to change or cancel their reservation themselves?',
                },
                YES_NO_DISCUSS,
            ),
            textareaField(
                'praktischeRegels',
                {
                    nl: 'Zijn er vaste check-in en check-out momenten of andere praktische regels?',
                    en: 'Are there fixed check-in and check-out times or other operational rules?',
                },
                { fullWidth: true },
            ),
        ],
    },
    {
        id: 'capaciteit',
        title: {
            nl: '5. Beschikbaarheid en capaciteit',
            en: '5. Availability and capacity',
        },
        fields: [
            radioField(
                'capaciteitJaNee',
                {
                    nl: 'Werken jullie met een maximumcapaciteit per dag of periode?',
                    en: 'Do you work with a maximum capacity per day or period?',
                },
                YES_NO_DISCUSS,
            ),
            textareaField(
                'capaciteitUitleg',
                {
                    nl: 'Zo ja, hoe wordt die capaciteit bepaald?',
                    en: 'If yes, how is that capacity determined?',
                },
                { fullWidth: false },
            ),
            textareaField('sluitingsdagen', {
                nl: 'Zijn er sluitingsdagen, vakantieperiodes of uitzonderlijke periodes?',
                en: 'Are there closing days, holidays, or exceptional periods?',
            }),
        ],
    },
    {
        id: 'klantEnHond',
        title: {
            nl: '6. Gegevens van klanten en honden',
            en: '6. Customer and dog information',
        },
        fields: [
            textareaField('eigenaarGegevens', {
                nl: 'Welke gegevens willen jullie minimaal verzamelen van de eigenaar?',
                en: 'What minimum details do you want to collect from the owner?',
            }),
            textareaField(
                'hondGegevens',
                {
                    nl: 'Welke gegevens willen jullie minimaal verzamelen van de hond?',
                    en: 'What minimum details do you want to collect from the dog?',
                },
                {
                    hint: {
                        nl: 'Bijvoorbeeld naam, ras, leeftijd, gewicht, gedrag, voeding, medicatie, vaccinaties, dierenarts, sociaal met andere honden…',
                        en: 'For example: name, breed, age, weight, behaviour, food, medication, vaccinations, vet, social behaviour with other dogs...',
                    },
                },
            ),
            radioField(
                'documentUpload',
                {
                    nl: 'Moeten klanten documenten kunnen uploaden, zoals vaccinatiebewijs?',
                    en: 'Should customers be able to upload documents, such as vaccination certificates?',
                },
                YES_NO_MAYBE,
            ),
            radioField(
                'accountVerplicht',
                {
                    nl: 'Willen jullie dat klanten een account moeten aanmaken?',
                    en: 'Do you want customers to create an account?',
                },
                [
                    option('required', 'Ja, verplicht', 'Yes, required'),
                    option('avoid', 'Nee, liever niet', 'No, preferably not'),
                    option('either', 'Mag allebei', 'Either is fine'),
                    option('discuss', 'Nog te bespreken', 'To be discussed'),
                ],
            ),
        ],
    },
    {
        id: 'betalingen',
        title: {
            nl: '7. Betalingen',
            en: '7. Payments',
        },
        fields: [
            radioField(
                'onlineBetalen',
                {
                    nl: 'Moeten klanten online kunnen betalen?',
                    en: 'Should customers be able to pay online?',
                },
                YES_NO_DISCUSS,
            ),
            checkboxField(
                'methodes',
                {
                    nl: 'Hoe willen jullie betalingen aanpakken?',
                    en: 'How do you want to handle payments?',
                },
                [
                    option('fullAdvance', 'Volledige betaling vooraf', 'Full payment in advance'),
                    option('deposit', 'Voorschot', 'Deposit'),
                    option('onSite', 'Betaling ter plaatse', 'Pay on site'),
                    option('bankTransfer', 'Overschrijving', 'Bank transfer'),
                    option('bancontact', 'Bancontact', 'Bancontact'),
                    option('creditCard', 'Kredietkaart', 'Credit card'),
                    option('discuss', 'Nog te bespreken', 'To be discussed'),
                ],
                { fullWidth: false, optionColumns: 1 },
            ),
            textareaField(
                'prijsregels',
                {
                    nl: 'Zijn er specifieke prijsregels waar rekening mee gehouden moet worden?',
                    en: 'Are there specific pricing rules we should take into account?',
                },
                {
                    hint: {
                        nl: 'Bijvoorbeeld meerdere honden, vakantieperiodes, extra diensten, medicatie of langere verblijven.',
                        en: 'For example: multiple dogs, holiday periods, extra services, medication, or longer stays.',
                    },
                },
            ),
        ],
    },
    {
        id: 'inhoud',
        title: {
            nl: '8. Inhoud en pagina’s',
            en: '8. Content and pages',
        },
        fields: [
            checkboxField(
                'paginas',
                {
                    nl: 'Welke pagina’s willen jullie zeker op de website?',
                    en: 'What pages do you definitely want on the website?',
                },
                [
                    option('home', 'Home', 'Home'),
                    option('about', 'Over ons', 'About us'),
                    option('services', 'Diensten', 'Services'),
                    option('pricing', 'Tarieven', 'Pricing'),
                    option('reservations', 'Reservatiepagina', 'Reservation page'),
                    option('contact', 'Contact', 'Contact'),
                    option('faq', 'FAQ', 'FAQ'),
                    option('reviews', 'Reviews / testimonials', 'Reviews / testimonials'),
                    option('gallery', 'Foto’s / galerij', 'Photos / gallery'),
                    option('terms', 'Voorwaarden', 'Terms'),
                    option('other', 'Andere', 'Other'),
                ],
                { optionColumns: 2 },
            ),
            radioField(
                'contentBeschikbaar',
                {
                    nl: 'Hebben jullie al teksten, foto’s, logo en huisstijl beschikbaar?',
                    en: 'Do you already have texts, photos, a logo, and brand style available?',
                },
                [
                    option('allReady', 'Ja, alles is al beschikbaar', 'Yes, everything is already available'),
                    option('partial', 'Gedeeltelijk', 'Partly'),
                    option('notReady', 'Nee, nog niet', 'No, not yet'),
                ],
            ),
            checkboxField(
                'talen',
                {
                    nl: 'Moet de website meertalig zijn?',
                    en: 'Should the website be multilingual?',
                },
                [
                    option('nl', 'Nederlands', 'Dutch'),
                    option('fr', 'Frans', 'French'),
                    option('en', 'Engels', 'English'),
                    option('notNeeded', 'Niet nodig', 'Not needed'),
                    option('discuss', 'Nog te bespreken', 'To be discussed'),
                ],
                { fullWidth: false, optionColumns: 1 },
            ),
        ],
    },
    {
        id: 'beheer',
        title: {
            nl: '9. Beheer en administratie',
            en: '9. Management and admin',
        },
        fields: [
            radioField(
                'beheerPaneel',
                {
                    nl: 'Willen jullie zelf reservaties en beschikbaarheid beheren via een beheerdersgedeelte?',
                    en: 'Do you want to manage reservations and availability yourselves through an admin area?',
                },
                YES_NO_DISCUSS,
            ),
            checkboxField(
                'beheerFuncties',
                {
                    nl: 'Wat moeten jullie zelf zeker kunnen beheren?',
                    en: 'What do you definitely want to manage yourselves?',
                },
                [
                    option('viewReservations', 'Reservaties bekijken', 'View reservations'),
                    option('approveReservations', 'Reservaties goedkeuren', 'Approve reservations'),
                    option('editAvailability', 'Beschikbaarheid aanpassen', 'Adjust availability'),
                    option('editPricing', 'Prijzen aanpassen', 'Adjust pricing'),
                    option('viewClients', 'Klantgegevens raadplegen', 'View client data'),
                    option('manageExtras', 'Extra diensten beheren', 'Manage extra services'),
                    option('setClosures', 'Sluitingsdagen instellen', 'Set closing days'),
                    option('other', 'Andere', 'Other'),
                ],
                { fullWidth: false, optionColumns: 1 },
            ),
            checkboxField(
                'emails',
                {
                    nl: 'Willen jullie automatische e-mails ontvangen of versturen?',
                    en: 'Do you want automatic emails to be sent or received?',
                },
                [
                    option('clientConfirmation', 'Bevestiging naar klant', 'Confirmation to client'),
                    option('internalAlert', 'Interne melding bij nieuwe reservatie', 'Internal alert for new reservation'),
                    option('reminder', 'Herinnering voor reservatie', 'Reservation reminder'),
                    option('cancellation', 'Annulatiebevestiging', 'Cancellation confirmation'),
                    option('other', 'Andere', 'Other'),
                    option('notNeeded', 'Niet nodig', 'Not needed'),
                ],
                { optionColumns: 2 },
            ),
        ],
    },
    {
        id: 'design',
        title: {
            nl: '10. Design en uitstraling',
            en: '10. Design and feel',
        },
        fields: [
            radioField(
                'uitstraling',
                {
                    nl: 'Welke uitstraling moet de website hebben?',
                    en: 'What feeling should the website have?',
                },
                [
                    option('warm', 'Warm en huiselijk', 'Warm and welcoming'),
                    option('professional', 'Professioneel en strak', 'Professional and clean'),
                    option('premium', 'Premium / luxueus', 'Premium / luxurious'),
                    option('playful', 'Speels en toegankelijk', 'Playful and approachable'),
                    option('undecided', 'Nog niet bepaald', 'Not decided yet'),
                ],
            ),
            textareaField(
                'voorbeeldenWebsites',
                {
                    nl: 'Zijn er websites die jullie mooi vinden of als voorbeeld zien?',
                    en: 'Are there websites you like or use as a reference?',
                },
                { fullWidth: false },
            ),
            textareaField('nietWillen', {
                nl: 'Zijn er zaken die jullie zeker niet willen op de website?',
                en: 'Is there anything you definitely do not want on the website?',
            }),
        ],
    },
    {
        id: 'planning',
        title: {
            nl: '11. Planning en budget',
            en: '11. Planning and budget',
        },
        fields: [
            textField('startdatum', {
                nl: 'Wanneer zouden jullie idealiter willen starten?',
                en: 'When would you ideally like to start?',
            }),
            textField('livedatum', {
                nl: 'Tegen wanneer willen jullie de website idealiter live hebben?',
                en: 'When would you ideally like the website to go live?',
            }),
            radioField(
                'budget',
                {
                    nl: 'Is er al een budget of budgetinschatting voorzien?',
                    en: 'Is there already a budget or budget estimate?',
                },
                [
                    option('undecided', 'Nog niet bepaald', 'Not decided yet'),
                    option('under1000', 'Minder dan €1.000', 'Less than €1,000'),
                    option('between1000and2500', '€1.000 – €2.500', '€1,000 - €2,500'),
                    option('between2500and5000', '€2.500 – €5.000', '€2,500 - €5,000'),
                    option('above5000', 'Meer dan €5.000', 'More than €5,000'),
                    option('discussable', 'Bespreekbaar', 'Discussable'),
                ],
                { fullWidth: true, optionColumns: 2 },
            ),
            textareaField('fase1Later', {
                nl: 'Zijn er zaken die absoluut in fase 1 moeten zitten, en zaken die eventueel later mogen komen?',
                en: 'Are there items that must be in phase 1, and items that can wait until later?',
            }),
        ],
    },
    {
        id: 'extra',
        title: {
            nl: '12. Extra info',
            en: '12. Extra info',
        },
        fields: [
            textareaField('extraInfo', {
                nl: 'Is er nog iets belangrijks dat we zeker moeten weten voor we een voorstel uitwerken?',
                en: 'Is there anything else we should know before preparing a proposal?',
            }),
        ],
    },
];

const getLocale = (language) => (language && language.startsWith('nl') ? 'nl' : 'en');

const getText = (value, locale) => {
    if (typeof value === 'string') {
        return value;
    }

    return value[locale] || value.en || value.nl || '';
};

const getInitialFormData = () =>
    FORM_SECTIONS.reduce((sectionAccumulator, section) => {
        const fieldValues = section.fields.reduce((fieldAccumulator, field) => {
            fieldAccumulator[field.key] = field.type === 'checkbox' ? [] : '';
            return fieldAccumulator;
        }, {});

        sectionAccumulator[section.id] = fieldValues;
        return sectionAccumulator;
    }, {});

const getOptionLabel = (field, value, locale) => {
    const matchedOption = (field.options || []).find((item) => item.value === value);
    return matchedOption ? getText(matchedOption.label, locale) : value;
};

const formatSummaryValue = (field, value, locale) => {
    if (Array.isArray(value)) {
        return value.length ? value.map((item) => getOptionLabel(field, item, locale)).join(', ') : '-';
    }

    if (!value) {
        return '-';
    }

    if (field.type === 'radio') {
        return getOptionLabel(field, value, locale);
    }

    return value;
};

const hasFormContent = (formData) =>
    Object.values(formData).some((sectionValues) =>
        Object.values(sectionValues).some((value) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            return Boolean(String(value || '').trim());
        }),
    );

const buildSummary = (formData, locale) => {
    let summary = `${PAGE_COPY[locale].summaryHeading}\n\n`;

    FORM_SECTIONS.forEach((section) => {
        summary += `${getText(section.title, locale).toUpperCase()}\n`;

        section.fields.forEach((field) => {
            const label = getText(field.label, locale);
            const value = formData[section.id][field.key];
            summary += `${label}: ${formatSummaryValue(field, value, locale)}\n`;
        });

        summary += '\n';
    });

    return summary.trim();
};

const optionGridClassNames = {
    1: 'grid gap-3',
    2: 'grid gap-3 md:grid-cols-2',
};

const Intake = () => {
    const { i18n } = useTranslation();
    const locale = getLocale(i18n.language);
    const copy = PAGE_COPY[locale];
    const [, setDocTitle] = useDocTitle(copy.docTitle);
    const [formData, setFormData] = useState(() => getInitialFormData());
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        setDocTitle(copy.docTitle);
    }, [copy.docTitle, setDocTitle]);

    const handleValueChange = (sectionId, field, nextValue) => {
        setFormData((currentData) => ({
            ...currentData,
            [sectionId]: {
                ...currentData[sectionId],
                [field.key]: nextValue,
            },
        }));
    };

    const handleCheckboxChange = (sectionId, field, optionValue) => {
        setFormData((currentData) => {
            const currentValues = currentData[sectionId][field.key];
            const nextValues = currentValues.includes(optionValue)
                ? currentValues.filter((item) => item !== optionValue)
                : [...currentValues, optionValue];

            return {
                ...currentData,
                [sectionId]: {
                    ...currentData[sectionId],
                    [field.key]: nextValues,
                },
            };
        });
    };

    const handleCopySummary = async () => {
        const summary = buildSummary(formData, locale);

        try {
            await navigator.clipboard.writeText(summary);
            Notiflix.Notify.success(copy.copySuccess);
        } catch (error) {
            Notiflix.Notify.failure(copy.copyError);
        }
    };

    const handleSendIntake = async () => {
        const basisgegevens = formData.basisgegevens;
        const summary = buildSummary(formData, locale);
        const contactName = basisgegevens.contactNaam.trim();
        const companyName = basisgegevens.bedrijfsnaam.trim();
        const senderName = companyName
            ? `${contactName || companyName} (${companyName})`
            : contactName;

        if (!contactName || !basisgegevens.email.trim()) {
            Notiflix.Report.failure('Missing details', copy.missingContact, 'Okay');
            return;
        }

        if (!EMAILJS_PUBLIC_KEY || !EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID) {
            Notiflix.Report.failure(
                'Email service not configured',
                'Set the EmailJS public key, service ID, and template ID before submitting the intake form.',
                'Okay',
            );
            return;
        }

        setIsSending(true);

        try {
            await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    first_name: contactName,
                    last_name: companyName,
                    name: senderName,
                    from_name: senderName,
                    user_name: senderName,
                    email: basisgegevens.email.trim(),
                    from_email: basisgegevens.email.trim(),
                    reply_to: basisgegevens.email.trim(),
                    user_email: basisgegevens.email.trim(),
                    phone: basisgegevens.telefoon.trim(),
                    phone_number: basisgegevens.telefoon.trim(),
                    website: basisgegevens.website.trim(),
                    company_name: companyName,
                    subject: `${copy.summaryHeading} - ${senderName}`,
                    message: summary,
                    intake_summary: summary,
                    intake_json: JSON.stringify(formData, null, 2),
                },
                {
                    publicKey: EMAILJS_PUBLIC_KEY,
                },
            );

            Notiflix.Report.success('Success', copy.sendSuccess, 'Okay');
            setFormData(getInitialFormData());
        } catch (sendError) {
            Notiflix.Report.failure(
                'An error occurred',
                sendError?.text || copy.sendError,
                'Okay',
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleReset = () => {
        setFormData(getInitialFormData());
    };

    const output = hasFormContent(formData)
        ? buildSummary(formData, locale)
        : copy.outputPlaceholder;

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,226,226,0.95),_transparent_32%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_42%,_#f1f5f9_100%)]">
            <main id="intake" className="relative overflow-hidden py-10 md:py-14">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden">
                    <div className="absolute left-[-8rem] top-16 h-56 w-56 rounded-full bg-red-200/60 blur-3xl" />
                    <div className="absolute right-[-5rem] top-10 h-64 w-64 rounded-full bg-amber-200/70 blur-3xl" />
                    <div className="absolute left-1/2 top-[-5rem] h-72 w-72 -translate-x-1/2 rounded-full bg-white/80 blur-3xl" />
                </div>

                <div className="relative mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
                    <div className="mb-6 flex justify-end" data-aos="fade-up">
                        <div className="inline-flex rounded-full border border-slate-200 bg-white/90 p-1 shadow-lg backdrop-blur">
                            <button
                                type="button"
                                onClick={() => i18n.changeLanguage('en')}
                                className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                                    locale === 'en'
                                        ? 'bg-red-900 text-white'
                                        : 'text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                EN
                            </button>
                            <button
                                type="button"
                                onClick={() => i18n.changeLanguage('nl')}
                                className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                                    locale === 'nl'
                                        ? 'bg-red-900 text-white'
                                        : 'text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                NL
                            </button>
                        </div>
                    </div>

                    <section
                        className="mb-10 grid gap-6 rounded-[2rem] border border-red-100 bg-white/90 p-8 shadow-2xl backdrop-blur md:grid-cols-[minmax(0,1.35fr)_minmax(0,0.85fr)] md:p-12"
                        data-aos="fade-up"
                    >
                        <div>
                            <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-4 py-1 text-xs font-black uppercase tracking-[0.28em] text-red-900">
                                {copy.standaloneBadge}
                            </span>
                            <p className="mb-3 mt-5 text-sm font-black uppercase tracking-[0.3em] text-red-900">
                                {copy.eyebrow}
                            </p>
                            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-900 md:text-5xl">
                                {copy.heroTitle}
                            </h1>
                            <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-600">
                                {copy.heroDescription}
                            </p>
                        </div>

                        <div className="rounded-[1.75rem] bg-gradient-to-br from-red-900 via-red-800 to-slate-900 p-7 text-white shadow-xl">
                            <p className="text-sm font-black uppercase tracking-[0.25em] text-red-100">
                                D&amp;IT
                            </p>
                            <p className="mt-4 text-sm leading-6 text-red-100">
                                {copy.standaloneDescription}
                            </p>
                            <ul className="mt-5 space-y-4">
                                {copy.heroPoints.map((point) => (
                                    <li
                                        key={point}
                                        className="flex items-start gap-3 text-base font-medium text-red-50"
                                    >
                                        <span className="mt-1 h-2.5 w-2.5 rounded-full bg-amber-300" />
                                        <span>{point}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    <form className="space-y-6">
                        <section
                            className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-slate-900">{copy.formTitle}</h2>
                                <p className="mt-2 max-w-3xl text-slate-600">{copy.formDescription}</p>
                            </div>

                            <div className="space-y-6">
                                {FORM_SECTIONS.map((section) => (
                                    <section
                                        key={section.id}
                                        className="rounded-[1.5rem] border border-slate-200/80 bg-slate-50/80 p-5 md:p-6"
                                    >
                                        <h3 className="mb-5 text-xl font-bold text-slate-900">
                                            {getText(section.title, locale)}
                                        </h3>

                                        <div className="grid gap-5 md:grid-cols-2">
                                            {section.fields.map((field) => {
                                                const fieldId = `${section.id}-${field.key}`;
                                                const value = formData[section.id][field.key];
                                                const containerClass = field.fullWidth
                                                    ? 'md:col-span-2'
                                                    : '';

                                                return (
                                                    <div key={fieldId} className={containerClass}>
                                                        <label
                                                            htmlFor={
                                                                field.type === 'radio' || field.type === 'checkbox'
                                                                    ? undefined
                                                                    : fieldId
                                                            }
                                                            className="mb-2 block text-sm font-bold text-slate-800"
                                                        >
                                                            {getText(field.label, locale)}
                                                        </label>

                                                        {field.hint && (
                                                            <p className="mb-3 text-sm text-slate-500">
                                                                {getText(field.hint, locale)}
                                                            </p>
                                                        )}

                                                        {(field.type === 'text' ||
                                                            field.type === 'email' ||
                                                            field.type === 'tel' ||
                                                            field.type === 'url') && (
                                                            <input
                                                                id={fieldId}
                                                                type={field.type}
                                                                value={value}
                                                                onChange={(event) =>
                                                                    handleValueChange(
                                                                        section.id,
                                                                        field,
                                                                        event.target.value,
                                                                    )
                                                                }
                                                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-red-300 focus:ring-4 focus:ring-red-100"
                                                            />
                                                        )}

                                                        {field.type === 'textarea' && (
                                                            <textarea
                                                                id={fieldId}
                                                                rows={field.rows}
                                                                value={value}
                                                                onChange={(event) =>
                                                                    handleValueChange(
                                                                        section.id,
                                                                        field,
                                                                        event.target.value,
                                                                    )
                                                                }
                                                                className="min-h-[8rem] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-red-300 focus:ring-4 focus:ring-red-100"
                                                            />
                                                        )}

                                                        {(field.type === 'radio' || field.type === 'checkbox') && (
                                                            <div
                                                                className={
                                                                    optionGridClassNames[field.optionColumns] ||
                                                                    optionGridClassNames[1]
                                                                }
                                                            >
                                                                {field.options.map((item) => {
                                                                    const optionId = `${fieldId}-${item.value}`;
                                                                    const checked =
                                                                        field.type === 'checkbox'
                                                                            ? value.includes(item.value)
                                                                            : value === item.value;

                                                                    return (
                                                                        <label
                                                                            key={item.value}
                                                                            className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                                                                                checked
                                                                                    ? 'border-red-300 bg-red-50'
                                                                                    : 'border-slate-200 bg-white hover:border-red-200'
                                                                            }`}
                                                                        >
                                                                            <input
                                                                                id={optionId}
                                                                                type={field.type}
                                                                                name={
                                                                                    field.type === 'radio'
                                                                                        ? fieldId
                                                                                        : undefined
                                                                                }
                                                                                checked={checked}
                                                                                onChange={() =>
                                                                                    field.type === 'checkbox'
                                                                                        ? handleCheckboxChange(
                                                                                              section.id,
                                                                                              field,
                                                                                              item.value,
                                                                                          )
                                                                                        : handleValueChange(
                                                                                              section.id,
                                                                                              field,
                                                                                              item.value,
                                                                                          )
                                                                                }
                                                                                className="mt-1 h-4 w-4 accent-red-900"
                                                                            />
                                                                            <span className="text-sm font-medium text-slate-700">
                                                                                {getText(item.label, locale)}
                                                                            </span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        </section>

                        <section
                            className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]"
                            data-aos="fade-up"
                            data-aos-delay="150"
                        >
                            <div className="rounded-[2rem] border border-red-100 bg-gradient-to-br from-red-900 via-red-800 to-slate-900 p-8 text-white shadow-2xl">
                                <h2 className="text-2xl font-black">{copy.actionsTitle}</h2>
                                <p className="mt-3 leading-7 text-red-50">{copy.actionsDescription}</p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={handleSendIntake}
                                        disabled={isSending}
                                        className="!rounded-2xl !bg-amber-300 !px-5 !py-3 !font-bold !text-slate-900 transition hover:!bg-amber-200 disabled:cursor-not-allowed disabled:opacity-70"
                                    >
                                        {isSending ? copy.sending : copy.sendIntake}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCopySummary}
                                        className="!rounded-2xl !border !border-white/30 !bg-white/10 !px-5 !py-3 !font-bold !text-white transition hover:!bg-white/20"
                                    >
                                        {copy.copySummary}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="!rounded-2xl !border !border-white/25 !bg-transparent !px-5 !py-3 !font-bold !text-white transition hover:!bg-white/10"
                                    >
                                        {copy.reset}
                                    </button>
                                </div>
                            </div>

                            <div className="rounded-[2rem] border border-slate-200 bg-slate-950 p-8 shadow-2xl">
                                <h2 className="text-2xl font-black text-white">{copy.outputTitle}</h2>
                                <pre className="mt-5 max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-[1.5rem] border border-slate-800 bg-slate-900/80 p-5 font-mono text-sm leading-7 text-slate-100">
                                    {output}
                                </pre>
                            </div>
                        </section>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default Intake;
